import { CdkScrollable } from "@angular/cdk/scrolling";
import { Component, OnInit, inject } from "@angular/core";
import { AbstractControl, FormGroup, ValidationErrors } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from "@angular/material/dialog";
import { LayoutAlignDirective, LayoutDirective } from "@ngbracket/ngx-layout/flex";
import { AttachmentsContainerComponent } from "@tylertech/attachments-module";
import { Command, CommandBarComponent, CommandComponent, IconCommand, LabelCommand } from "@tylertech/commands-module";
import { BusyIndicatorComponent } from "@tylertech/common-components-module";
import { SecurityService } from "@tylertech/core-module";
import { ForgeIconButtonModule, ForgeIconModule, ForgeTooltipModule } from "@tylertech/forge-angular";
import { NotesComponent } from "@tylertech/notes-module";
import { Utilities } from "@tylertech/shared-module";
import { AttachmentViewModel, BidQuoteNoteViewModel, BidQuoteViewModel, INote, NoteViewModel, } from "@tylertech/tyler-api-types";
import { BidQuoteModel } from "../../interfaces";
import { RequisitionService } from "../../services";
import { BidQuotesComponent } from "./bidQuotes.component";

enum BidQuotePanel
{
    BidQuotes = 0,
    Notes,
    Attachments,
}
type BidQuoteError = "moreThanOneAwarded" | "invalid" | "invalidNotes" | "invalidAttachments";
export interface BidQuotesDialogData
{
    readonly bidQuotes: ReadonlyArray<BidQuoteModel>;
    readonly canEdit: boolean;
    /** @deprecated NOT USED */
    readonly multi: boolean;
    readonly reqId: string;
}
@Component({
    selector: "bid-quotes-dialog-component",
    templateUrl: "./bidQuotesDialog.tmpl.html",
    imports: [
        AttachmentsContainerComponent,
        BidQuotesComponent,
        BusyIndicatorComponent,
        CdkScrollable,
        CommandBarComponent,
        CommandComponent,
        LayoutAlignDirective,
        LayoutDirective,
        ForgeIconButtonModule,
        ForgeIconModule,
        ForgeTooltipModule,
        MatDialogActions,
        MatDialogContent,
        MatDialogTitle,
        NotesComponent,
    ]
})
export class BidQuotesDialogComponent implements OnInit
{
    private readonly dialogRef = inject<MatDialogRef<BidQuotesDialogComponent, Array<BidQuoteViewModel> | undefined>>(MatDialogRef);
    private readonly data = inject<BidQuotesDialogData>(MAT_DIALOG_DATA);
    private $securityService = inject(SecurityService);
    private $requisitionService = inject(RequisitionService);

    readonly formGroup: FormGroup;
    bidQuotes: BidQuoteModel[];
    /** @deprecated */ private _current: BidQuoteModel;
    /** @deprecated */ currentAttachments: AttachmentViewModel[] = [];
    /** @deprecated */ currentNotes: BidQuoteNoteViewModel[] = [];
    /** @deprecated */ private _selectedQuote: BidQuoteModel;
    private currentPanel: BidQuotePanel = BidQuotePanel.BidQuotes;
    readonly backCommand: Readonly<Command>;
    readonly dialogCommands: ReadonlyArray<Command>;
    isLoaded: boolean;
    constructor()
    {
        this.formGroup = new FormGroup({}, [
            // TODO - Total process validators
            (_control: AbstractControl): ValidationErrors | null =>
            {
                if (this.bidQuotes?.filter(x => x.Awarded).length > 1)
                {
                    return {
                        multipleAwarded: true,
                    };
                }
                return null;
            },
            (_control: AbstractControl): ValidationErrors | null =>
            {
                // TODO - validate all bidQuotes have Vendor
                if (this.bidQuotes?.filter(x => !x.IsDeleted && !x.Vendor).length > 0)
                {
                    return {
                        invalidBidQuote: true,
                    };
                }
                return null;
            },
            (_control: AbstractControl): ValidationErrors | null =>
            {
                // TODO - validate all bidQuotes have a non-zero Total
                if (this.bidQuotes?.filter(x => !x.IsDeleted && !x.Total).length > 0)
                {
                    return {
                        invalidBidQuote: true,
                    };
                }
                return null;
            },
            (_control: AbstractControl): ValidationErrors | null =>
            {
                // TODO - validate all bidQuotes are either not Awarded or have an AwardDate
                if (this.bidQuotes?.filter(x => !x.IsDeleted && x.Awarded && !x.AwardDate).length > 0)
                {
                    return {
                        invalidBidQuote: true,
                    };
                }
                return null;
            },
            (_control: AbstractControl): ValidationErrors | null =>
            {
                // TODO - validate all Attachments;
                const invalidAttachments = this.currentAttachments.filter(
                    x => !x.IsDeleted && !!x.Type && !!x.AddedBy && !!x.AddedDate && !!x.Filename
                );
                if (invalidAttachments.length > 0)
                {
                    return {
                        invalidAttachment: true,
                    };
                }
                return null;
            },
            (_control: AbstractControl): ValidationErrors | null =>
            {
                // TODO - validate all Notes
                const invalidNotes = this.currentNotes?.filter(x => !this.isNoteValid(x));
                if (invalidNotes.length > 0)
                {
                    return {
                        invalidNote: true,
                    };
                }
                return null;
            },
        ]);
        this.backCommand = new IconCommand({
            icon: "arrow_back",
            label: null,
            action: () => this.goBack(),
            getEnabled: () => true,
        });
        this.dialogCommands = [
            new LabelCommand({
                label: "Cancel",
                action: () => this.cancel(),
                getEnabled: () => this.canEdit,
                tooltip: "",
                getVisible: () => this.showBidQuotes && this.canEdit,
            }),
            new LabelCommand({
                type: "raised",
                label: this.canEdit ? "Done" : "Close",
                action: () => (this.canEdit ? this.close() : this.cancel()),
                getEnabled: () => true,
                tooltip: "",
                getVisible: () => this.showBidQuotes,
            }),
        ];
    }

    ngOnInit(): void
    {
        if (this.reqId && Utilities.IsNullOrUndefinedOrZero(this.originalBidQuotes))
        {
            this.$requisitionService.GetBidQuotes(this.reqId).then((result: Array<BidQuoteModel>) =>
            {
                this.bidQuotes = result;
                this.isLoaded = true;
            });
        }
        else
        {
            this.bidQuotes = [];
            if (this.originalBidQuotes?.length > 0)
            {
                // ?Review: this is a shallow copy, i don't know if it should be a deep copy.
                this.bidQuotes = this.originalBidQuotes.map(x => Object.assign({}, x));
            }
            if (this.originalBidQuotes.length === 0)
            {
                this.addBidQuote();
            }
        }
    }

    private get originalBidQuotes(): ReadonlyArray<BidQuoteModel>
    {
        return this.data.bidQuotes;
    }

    get canEdit(): boolean
    {
        return this.data.canEdit;
    }

    private get multi(): boolean
    {
        return this.data.multi;
    }

    private get reqId(): string
    {
        return this.data.reqId;
    }

    get showBidQuotes(): boolean
    {
        return this.currentPanel === BidQuotePanel.BidQuotes;
    }

    get showAttachments(): boolean
    {
        return this.currentPanel === BidQuotePanel.Attachments;
    }

    get showNotes(): boolean
    {
        return this.currentPanel === BidQuotePanel.Notes;
    }

    get hasInvalidBidQuoteError(): boolean
    {
        return this.hasErrors && this.formGroup.errors["invalidBidQuote"];
    }

    get hasInvalidNoteError(): boolean
    {
        return this.hasErrors && this.formGroup.errors["invalidNote"];
    }

    get hasInvalidAttachmentError(): boolean
    {
        return this.hasErrors && this.formGroup.errors["invalidAttachment"];
    }

    get hasMultipleAwardedError(): boolean
    {
        return this.hasErrors && this.formGroup.errors["multipleAwarded"];
    }

    get hasErrors(): boolean
    {
        return !Utilities.IsNullOrUndefined(this.formGroup.errors);
    }

    get Title(): string
    {
        switch (this.currentPanel)
        {
            case BidQuotePanel.Notes:
                return "Bid/Quote Notes";
            case BidQuotePanel.Attachments:
                return "Bid/Quote Attachments";
            default:
                return "Bid/Quotes";
        }
    }

    get PanelName(): string
    {
        switch (this.currentPanel)
        {
            case BidQuotePanel.Notes:
                return "Note";
            case BidQuotePanel.Attachments:
                return "Attachment";
            default:
                return "Bid/Quote";
        }
    }

    addAction(): void
    {
        switch (this.currentPanel)
        {
            case BidQuotePanel.BidQuotes:
                this.addBidQuote();
                break;
            case BidQuotePanel.Attachments:
                this.addAttachment();
                break;
            default:
                this.addNote();
                break;
        }
    }

    private addAttachment(): void
    {
        if (Utilities.IsNullOrUndefined(this._current))
        {
            throw "BidQuotesDialogController.addAttachment | A selected bid/Quote is required.";
        }
        throw new Error("Not Implemented.");
        // TODO - this was dumb, do not reach into other components internals and try to manipulate them...
        // const d = document.getElementById(this.fileUploadId);
        // d.click();
    }

    private addBidQuote(): void
    {
        this.bidQuotes.push(<BidQuoteModel>{
            Vendor: undefined,
            Total: undefined,
            Awarded: undefined,
            AwardDate: undefined,
            Attachments: [],
            Notes: [],
            IsDeleted: false,
            IsNew: true,
            Id: undefined,
            RecordVersion: 0,
        });
    }

    private addNote(): void
    {
        if (Utilities.IsNullOrUndefined(this._current))
        {
            throw "BidQuotesDialogController.addNote | A selected bid/Quote is required.";
        }
        const newNote = <BidQuoteNoteViewModel>{
            CreatedDate: new Date(Date.now()),
            CreatedBy: this.$securityService.principle.Username,
            IsDeleted: false,
            IsNew: true,
            Description: undefined,
            Id: undefined,
            RecordVersion: undefined,
            Subject: undefined,
        };
        this.currentNotes.push(newNote);
    }

    appendAttachments(attachments: AttachmentViewModel[]): void
    {
        if (Utilities.IsNullOrUndefined(this._current))
        {
            throw "BidQuotesDialogController.appendAttachments | A selected bid/Quote is required.";
        }
        this.currentAttachments.push(...attachments);
    }

    private cancel(): void
    {
        this.dialogRef.close();
    }

    private close(): void
    {
        if (this.formGroup.invalid)
        {
            return;
        }
        this.dialogRef.close(this.bidQuotes);
    }

    deleteBidQuote(selected: BidQuoteModel): void
    {
        if (Utilities.IsNullOrUndefined(selected))
        {
            return;
        }
        if (!selected.IsNew)
        {
            selected.IsDeleted = true;
            return;
        }
        const index = this.bidQuotes.indexOf(selected);
        if (index === -1)
        {
            return;
        }
        this.bidQuotes = this.bidQuotes.filter(x => x !== selected);
    }

    deleteNote($event: INote): void
    {
        if (Utilities.IsNullOrUndefined(this._current))
        {
            throw "BidQuotesDialogController.deleteNote | A selected bid/Quote is required.";
        }
        if (!$event.IsNew)
        {
            $event.IsDeleted = true;
            return;
        }
        this.currentNotes = this.currentNotes.filter(x => x !== $event);
    }

    private goBack(): void
    {
        if (this.formGroup.invalid)
        {
            return;
        }
        switch (this.currentPanel)
        {
            case BidQuotePanel.Attachments:
                this._current.Attachments = this.currentAttachments;
                this.currentAttachments.length = 0;
                break;
            case BidQuotePanel.Notes:
                this._current.Notes = this.currentNotes;
                this.currentNotes.length = 0;
                break;
            default:
                throw `BidQuotesDialogController.goBack | Unexpected Visible Panel[${this.currentPanel}]: ${BidQuotePanel[this.currentPanel]
                }`;
        }
        this.currentPanel = BidQuotePanel.BidQuotes;
        this._current = undefined;
    }

    removeAttachment(attachment: AttachmentViewModel): void
    {
        if (!this._current)
        {
            throw "BidQuotesDialogController.removeAttachment | A selected bid/Quote is required.";
        }
        if (!attachment.IsNew)
        {
            attachment.IsDeleted = true;
            return;
        }
        this.currentAttachments = this.currentAttachments.filter(x => x !== attachment);
    }

    switchToAttachments(selected: BidQuoteModel): void
    {
        this.currentPanel = BidQuotePanel.Attachments;
        this._current = selected;
        if (!Utilities.IsNullOrUndefined(this._current.Attachments))
        {
            this.currentAttachments = this.currentAttachments.concat(this._current.Attachments);
        }
    }

    switchToNotes(selected: BidQuoteModel): void
    {
        this.currentPanel = BidQuotePanel.Notes;
        this._current = selected;
        if (this._current.Notes)
        {
            this.currentNotes = this.currentNotes.concat(this._current.Notes);
        }
    }

    private isNoteValid(note: NoteViewModel): boolean
    {
        if (note.IsDeleted)
        {
            return true;
        }
        return !!note.Subject && !!note.Description && !!note.CreatedBy && !!note.CreatedDate;
    }
}
